import { useCallback, useEffect, useMemo, useReducer, useState, type FormEvent, type ReactNode, type Ref } from "react";
import { QRCodeSVG } from "qrcode.react";
import { passExtendedExpiry } from "../../worker/src/domain";
import { api, patch, post, put, remove } from "../api";
import { useAdminDetailFocus } from "../admin-detail-focus";
import { AttendanceChargeBadge, AttendanceMeta } from "../components/Attendance";
import { AttendanceStatusBadge } from "../components/AttendanceStatusBadge";
import { ActionMenu, ActionMenuDivider, ActionMenuItem } from "../components/ActionMenu";
import { AdminShell, Notice } from "../components/Layout";
import { SchoolDateContext } from "../components/SchoolDateContext";
import { AttendanceActions, CorrectionsPanel, HistoricalAttendanceModal, PassActivationModal, PaymentActions, SessionActions } from "../components/AdminCorrections";
import { EditClassForm } from "../components/EditClassForm";
import { PassExtensionModal, type PassExtensionPreviewItem } from "../components/PassExtensionModal";
import { useI18n } from "../i18n";
import { openClassroomDisplay } from "../kiosk-lock";
import { passDisplayStatus, passStatusLabel } from "../pass-status";
import type { AdminLiveResponse, AdminResponse, AdminStudent, Pass, Payment, RegularClassTemplate,
  Session, SessionAttendanceResponse, StudentHistoryResponse } from "../types";
import { adminLiveReducer, groupAttendanceBySession, initialAdminLiveState, refreshAdminSnapshots } from "../admin-live-state";
import { dateInTimeZone, timeInTimeZone } from "../time";

type Tab = "today" | "students" | "sessions" | "passes" | "payments" | "emails" | "corrections" | "staff";
type Mutate = <T>(action: () => Promise<T>, success?: string) => Promise<T | undefined>;
interface InvitationResponse { ok: true; invitationUrl: string }
interface CreateSessionResponse { ok: true; sessionId: string }
interface PassExtensionPreviewResponse { ok: true; extensionDays: number; affectedCount: number; passes: PassExtensionPreviewItem[] }
interface CutoffPreviewResponse {
  ok: true; cutoff: string; removable: number; preserved: number;
  affectedSessions: Array<{ session_id: string; title: string; class_date: string; start_time: string }>;
}

function OpenClassroomDisplayButton({ sessionId, compact = false }: { sessionId: string; compact?: boolean }) {
  return <button type="button" className={(compact ? "secondary-button compact" : "primary-button compact") + " open-kiosk-button"}
    onClick={() => { void openClassroomDisplay(sessionId); }}>Classroom Display</button>;
}

function ExportLinks({ role, className = "" }: { role: AdminResponse["staff"]["role"]; className?: string }) {
  return <div className={`export-links ${className}`.trim()}>
    <a className="secondary-link compact" href="/api/admin/export/attendance.csv">Attendance CSV</a>
    {role === "owner" && <a className="secondary-link compact" href="/api/admin/export/attendance.csv?includeVoided=1">Attendance + voided CSV</a>}
    <a className="secondary-link compact" href="/api/admin/export/students.csv">Students CSV</a>
    <a className="secondary-link compact" href="/api/admin/export/passes.csv">Passes CSV</a>
  </div>;
}

export function AdminPage() {
  const { t } = useI18n();
  const [data, setData] = useState<AdminResponse | null>(null);
  const [liveState, dispatchLive] = useReducer(adminLiveReducer, initialAdminLiveState);
  const [tab, setTab] = useState<Tab>(() => new URLSearchParams(window.location.search).get("view") === "sessions" ? "sessions" : "today");
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [working, setWorking] = useState(false);
  const load = useCallback(async (quiet = false) => {
    try { setData(await api<AdminResponse>("/api/admin/bootstrap")); if (!quiet) setError(""); }
    catch (reason) { if (!quiet) setError(reason instanceof Error ? reason.message : t("signIn")); }
  }, [t]);
  const loadLive = useCallback(async () => {
    try {
      dispatchLive({ type: "success", data: await api<AdminLiveResponse>("/api/admin/live") });
    } catch {
      dispatchLive({ type: "failure" });
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    if (tab !== "today" || !data) return;
    const refresh = () => { void loadLive(); };
    const refreshWhenVisible = () => { if (document.visibilityState === "visible") refresh(); };
    refresh();
    const timer = window.setInterval(refresh, 5_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refreshWhenVisible);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [data, loadLive, tab]);
  async function mutate<T>(action: () => Promise<T>, success: string = t("save")): Promise<T | undefined> {
    setWorking(true); setError(""); setMessage("");
    try {
      const result = await action();
      setMessage(success);
      await refreshAdminSnapshots(() => load(true), loadLive);
      return result;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : t("error"));
      return undefined;
    } finally { setWorking(false); }
  }
  const canWrite = data?.staff.role === "owner";
  return <AdminShell title={t("admin")}>
    {error && <Notice kind="error">{error}</Notice>}{message && <Notice kind="success">{message}</Notice>}
    {tab === "today" && liveState.paused && <Notice>Live updates paused. Showing the last successful data while retrying automatically.</Notice>}
    {!data && !error && <div className="loading-card">{t("loading")}</div>}
    {data && <div className="admin-layout">
      <aside className="admin-sidebar"><div className="staff-card"><strong>{data.staff.displayName}</strong><span>{data.staff.role}</span></div>
        <nav className="admin-nav" aria-label="Admin sections">
          {(["today", "students", "sessions", "passes", "payments", "emails", ...(data.staff.role === "owner" ? ["corrections" as Tab, "staff" as Tab] : [])] as Tab[]).map((item) =>
            <button key={item} className={tab === item ? "active" : ""} aria-current={tab === item ? "page" : undefined} onClick={() => setTab(item)}>
              {item === "staff" ? "Staff" : item === "corrections" ? "Corrections" : t(item === "today" ? "today" : item)}
            </button>)}
        </nav>
        <ExportLinks role={data.staff.role} className="export-links-desktop" />
        <details className="export-menu-mobile">
          <summary>Exports</summary>
          <ExportLinks role={data.staff.role} />
        </details>
      </aside>
      <section className="admin-content">
        {tab === "today" && <Today data={data} live={liveState.data} working={working} mutate={mutate} />}
        {tab === "students" && <Students data={data} canWrite={canWrite} working={working} mutate={mutate} />}
        {tab === "sessions" && <Sessions data={data} canWrite={canWrite} working={working} mutate={mutate} />}
        {tab === "passes" && <Passes data={data} canWrite={canWrite} working={working} mutate={mutate} />}
        {tab === "payments" && <Payments data={data} canWrite={canWrite} working={working} mutate={mutate} />}
        {tab === "emails" && <Emails data={data} />}
        {tab === "corrections" && data.staff.role === "owner" && <CorrectionsPanel />}
        {tab === "staff" && data.staff.role === "owner" && <Staff data={data} working={working} mutate={mutate} />}
      </section>
    </div>}
  </AdminShell>;
}
function Today({ data, live, working, mutate }: { data: AdminResponse; live: AdminLiveResponse | null; working: boolean; mutate: Mutate }) {
  const { t } = useI18n();
  const today = live?.today ?? dateInTimeZone(data.settings.timeZone);
  const attendance = live?.attendance ?? data.attendance.filter((row) => row.class_date === today);
  const unpaid = live?.payments ?? data.payments.filter((payment) => payment.status === "unpaid");
  const activePasses = live?.passes ?? data.passes.filter((pass) => pass.status === "active");
  const currentPasses = activePasses.filter((pass) => passDisplayStatus(pass, today) === "active");
  const todaySessions = live?.sessions ?? data.sessions.filter((session) => session.class_date === today);
  const attendanceBySession = useMemo(() => groupAttendanceBySession(attendance), [attendance]);

  const lowPasses = currentPasses.filter((pass) => pass.pass_type === "count" && Number(pass.remaining_classes) <= 2);
  const expiring = currentPasses.filter((pass) => pass.expiry_date && daysUntil(pass.expiry_date, today) <= 14);
  const passPendingStudentIds = new Set(attendance.filter((row) => row.billing_intent === "pass_pending").map((row) => row.student_id));
  const noPassStudentIds = new Set(attendance.filter((row) => row.charge_type === "dropin" && !row.billing_intent).map((row) => row.student_id));
  return <div className="admin-stack">
    <div className="stat-grid">
      <Stat label={t("attendance")} value={attendance.length} />
      <Stat label={t("paymentDue")} value={unpaid.length} />
      <Stat label={t("passes")} value={currentPasses.length} />
      <Stat label={t("sessions")} value={todaySessions.length} />
    </div>
    <Panel title={t("sessions")}><p className="school-date-note">School date: {today} ({data.settings.timeZone})</p><div className="session-cards">
      {todaySessions.length ? todaySessions.map((session) => {
        const rows = attendanceBySession.get(session.session_id) ?? [];
        return <article className="session-card" key={session.session_id}>
          <div className="session-card-heading">
            <div><span>{session.start_time}</span><h3>{session.title}</h3><p>{session.teacher_name}</p></div>
            {data.staff.role === "owner" && <OpenClassroomDisplayButton sessionId={session.session_id} />}
          </div>
          <div className="session-roster">
            <strong>{rows.length} {t("attendance")}</strong>
            {rows.length ? <div className="session-attendees">{rows.map((row) => <div className="session-attendee" key={row.attendance_id}>
              <span>{row.display_name}</span>
              <time dateTime={row.checked_in_at}>{timeInTimeZone(row.checked_in_at, data.settings.timeZone)}</time>
              <AttendanceChargeBadge attendance={row} />
              <AttendanceActions row={row} data={data} working={working} mutate={mutate} />
            </div>)}</div> : <p className="muted">No check-ins yet.</p>}
          </div>
        </article>;
      }) : <Empty />}
    </div></Panel>
    <Panel title={t("followUp")}><div className="follow-list">
      {unpaid.map((payment) => <Follow key={payment.payment_id} label={t("paymentDue")} value={studentName(data, payment.student_id) + " / " + money(payment.amount_minor, payment.currency)} />)}
      {lowPasses.map((pass) => <Follow key={pass.pass_id} label={t("lowPass")} value={studentName(data, pass.student_id) + " / " + pass.remaining_classes + "/" + pass.total_classes} />)}
      {expiring.map((pass) => <Follow key={"exp-" + pass.pass_id} label={t("expiring")} value={studentName(data, pass.student_id) + " / " + pass.expiry_date} />)}
      {[...passPendingStudentIds].map((studentId) => <Follow key={"pending-" + studentId} label="Pass purchase pending" value={studentName(data, studentId)} />)}
      {[...noPassStudentIds].map((studentId) => <Follow key={"drop-" + studentId} label={t("noValidPass")} value={studentName(data, studentId)} />)}
      {!unpaid.length && !lowPasses.length && !expiring.length && !noPassStudentIds.size && !passPendingStudentIds.size && <Empty />}
    </div></Panel>
    <Panel title={t("attendance")}><AttendanceList rows={attendance} data={data} working={working} mutate={mutate} /></Panel>
  </div>;
}

function Students({ data, canWrite, working, mutate }: SectionProps) {
  const { t } = useI18n();
  const [invite, setInvite] = useState<{ displayName: string; url: string } | null>(null);
  const [detail, setDetail] = useState<StudentHistoryResponse | null>(null);
  const [detailFocusVersion, setDetailFocusVersion] = useState(0);
  const [detailError, setDetailError] = useState("");
  async function viewStudent(studentId: string, moveFocus = true) {
    setDetailError("");
    try {
      setDetail(await api<StudentHistoryResponse>(`/api/admin/students/${encodeURIComponent(studentId)}/history`));
      if (moveFocus) setDetailFocusVersion((current) => current + 1);
    }
    catch (reason) { setDetailError(reason instanceof Error ? reason.message : "Could not load student history"); }
  }
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = event.currentTarget; const value = new FormData(form);
    const displayName = String(value.get("displayName") || "");
    const result = await mutate(() => post<InvitationResponse>("/api/admin/students", {
      displayName, email: value.get("email"), phone: value.get("phone"), emailConsent: value.get("emailConsent") === "on",
    }), t("invite"));
    if (result?.invitationUrl) { setInvite({ displayName, url: result.invitationUrl }); form.reset(); }
  }
  async function resend(student: AdminStudent) {
    const result = await mutate(() => post<InvitationResponse>("/api/admin/students/" + student.student_id + "/invitations", {}), t("invite"));
    if (result?.invitationUrl) setInvite({ displayName: student.display_name, url: result.invitationUrl });
  }
  async function importCsv(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const file = form.querySelector<HTMLInputElement>('[type="file"]')?.files?.[0];
    if (!file) return;
    const students = parseStudentCsv(await file.text());
    const result = await mutate(() => post("/api/admin/import/students", { students }), students.length + " students imported");
    if (result) form.reset();
  }
  return <div className="admin-stack">
    {invite && <InviteShareCard displayName={invite.displayName} url={invite.url} />}
    {detailError && <Notice kind="error">{detailError}</Notice>}
    {detail && <StudentHistoryDetail data={detail} adminData={data} focusKey={detailFocusVersion} staffRole={data.staff.role}
      working={working} mutate={mutate} onRefresh={() => viewStudent(detail.student.student_id, false)} onClose={() => setDetail(null)} />}
    {canWrite && <Panel title={t("newStudent")}><form className="form-grid" onSubmit={create}>
      <Field name="displayName" label={t("displayName")} required />
      <Field name="email" label={t("email")} type="email" required />
      <Field name="phone" label={t("phone")} />
      <label className="check-label"><input name="emailConsent" type="checkbox" defaultChecked /><span>{t("consent")}</span></label>
      <button className="primary-button" disabled={working}>{t("invite")}</button>
    </form></Panel>}
    {canWrite && <Panel title="Import students from CSV"><form className="form-grid" onSubmit={importCsv}><label><span>CSV file</span><input type="file" accept=".csv,text/csv" required /></label><button className="secondary-button" disabled={working}>Import CSV</button></form></Panel>}
    <Panel title={t("students")}><div className="data-list">
      {data.students.map((student) => <article className="data-row" key={student.student_id}>
        <div><strong>{student.display_name}</strong><span>{student.email}</span><small className="inline-meta"><span>{student.status}</span><span>{student.email_consent ? "email on" : "email off"}</span></small></div>
        <div className="row-actions">
          <button className="primary-button compact" disabled={working} onClick={() => void viewStudent(student.student_id)}>View student</button>
          {canWrite && <ActionMenu><ActionMenuItem disabled={working} onClick={() => void resend(student)}>Resend invite</ActionMenuItem></ActionMenu>}
        </div>
      </article>)}
    </div></Panel>
  </div>;
}

function InviteShareCard({ displayName, url }: { displayName: string; url: string }) {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  async function copyInvite() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      window.prompt(t("copyLink"), url);
    }
  }
  async function shareInvite() {
    if (!navigator.share) return copyInvite();
    try {
      await navigator.share({ title: displayName + " - " + t("inviteReady"), text: t("claimHelp"), url });
    } catch (error) {
      if (!(error instanceof DOMException && error.name === "AbortError")) await copyInvite();
    }
  }
  return <Panel title={t("inviteReady")}><div className="invite-share">
    <div className="invite-qr"><QRCodeSVG value={url} size={184} level="M" title={`${displayName} invitation QR`} /></div>
    <div className="invite-details"><strong>{displayName}</strong><p>{t("inviteHelp")}</p><input aria-label={t("copyLink")} value={url} readOnly />
      <div className="row-actions"><button className="primary-button" type="button" onClick={() => void shareInvite()}>{t("shareInvite")}</button>
        <button className="secondary-button" type="button" onClick={() => void copyInvite()}>{copied ? t("copied") : t("copyLink")}</button></div>
    </div>
  </div></Panel>;
}
const WEEKDAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function instructorOptions(data: AdminResponse) {
  return (data.staffMembers || []).filter((staff) => staff.active);
}

function defaultInstructorId(data: AdminResponse) {
  return instructorOptions(data).find((staff) => staff.role === "owner")?.staff_id || "";
}

function InstructorSelect({ data, defaultValue }: { data: AdminResponse; defaultValue?: string }) {
  const options = instructorOptions(data);
  return <label><span>Instructor</span><select name="instructorStaffId" defaultValue={defaultValue || defaultInstructorId(data)}>
    {options.map((staff) => <option key={staff.staff_id} value={staff.staff_id}>{staff.display_name}</option>)}
  </select></label>;
}

export function RegularTimetable({ data, working, mutate }: {
  data: AdminResponse; working: boolean; mutate: Mutate;
}) {
  const [editing, setEditing] = useState<RegularClassTemplate | null>(null);
  const [cutoffTemplate, setCutoffTemplate] = useState<RegularClassTemplate | null>(null);
  const [deletingTemplate, setDeletingTemplate] = useState<RegularClassTemplate | null>(null);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget, value = new FormData(form);
    const payload = {
      title: value.get("title"),
      weekday: Number(value.get("weekday")),
      startTime: value.get("startTime"),
      endTime: value.get("endTime"),
      instructorStaffId: value.get("instructorStaffId") || undefined,
    };
    const result = editing
      ? await mutate(() => put("/api/admin/class-templates/" + encodeURIComponent(editing.class_id), payload), "Regular class updated")
      : await mutate(() => post("/api/admin/class-templates", payload), "Regular class created");
    if (result) { setEditing(null); form.reset(); }
  }
  async function toggle(template: RegularClassTemplate) {
    await mutate(() => patch("/api/admin/class-templates/" + encodeURIComponent(template.class_id) + "/status",
      { active: !template.active }), template.active ? "Regular class paused" : "Regular class resumed");
  }
  return <Panel title="Regular Timetable">
    {cutoffTemplate && <RegularCutoffModal template={cutoffTemplate} working={working} mutate={mutate}
      schoolToday={dateInTimeZone(data.settings.timeZone)} onClose={() => setCutoffTemplate(null)} />}
    {deletingTemplate && <DeleteRegularClassModal template={deletingTemplate} working={working} mutate={mutate}
      onClose={() => setDeletingTemplate(null)} />}
    <form key={editing?.class_id || "new-template"} className="form-grid regular-template-form" onSubmit={save}>
      <Field name="title" label="Class name" required defaultValue={editing?.title} />
      <label><span>Weekday</span><select name="weekday" defaultValue={String(editing?.weekday ?? 1)}>
        {WEEKDAYS.map((day, index) => <option key={day} value={index}>{day}</option>)}
      </select></label>
      <Field name="startTime" label="Start" type="time" required defaultValue={editing?.start_time || "17:00"} />
      <Field name="endTime" label="End" type="time" required defaultValue={editing?.end_time || "18:00"} />
      <InstructorSelect data={data} defaultValue={editing?.instructor_staff_id} />
      <div className="row-actions">
        <button className="primary-button" disabled={working}>{editing ? "Save changes" : "Add regular class"}</button>
        {editing && <button type="button" className="secondary-button" onClick={() => setEditing(null)}>Cancel edit</button>}
      </div>
    </form>
    <div className="data-list regular-template-list">
      {data.classTemplates.length ? data.classTemplates.map((template) => <article className="data-row" key={template.class_id}>
        <div><strong>{template.title}</strong>
          <span>{WEEKDAYS[template.weekday]} · {template.start_time}–{template.end_time}</span>
          <small>{template.instructor_name}</small>
          <small className={"status-badge " + (template.active ? "status-active" : "status-paused")}>
            {template.active ? "Active" : "Paused"}
          </small>
          {template.inactive_from_date && <small>Runs until {template.inactive_from_date} (not included)</small>}
        </div>
        <div className="row-actions">
          <button type="button" className="primary-button compact" disabled={working} onClick={() => setEditing(template)}>Edit</button>
          <ActionMenu>
            <ActionMenuItem disabled={working} onClick={() => void toggle(template)}>{template.active ? "Pause" : "Resume"}</ActionMenuItem>
            <ActionMenuItem disabled={working} onClick={() => setCutoffTemplate(template)}>Remove from date</ActionMenuItem>
            <ActionMenuDivider />
            <ActionMenuItem className="danger-action" disabled={working} onClick={() => setDeletingTemplate(template)}>Delete regular class</ActionMenuItem>
          </ActionMenu>
        </div>
      </article>) : <p className="muted">No regular classes yet.</p>}
    </div>
  </Panel>;
}

function RegularCutoffModal({ template, schoolToday, working, mutate, onClose }: {
  template: RegularClassTemplate; schoolToday: string; working: boolean; mutate: Mutate; onClose: () => void;
}) {
  const [cutoff, setCutoff] = useState(template.inactive_from_date || schoolToday);
  const [preview, setPreview] = useState<CutoffPreviewResponse | null>(null);
  const [previewError, setPreviewError] = useState("");
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  useEffect(() => {
    let current = true;
    setPreview(null); setPreviewError("");
    api<CutoffPreviewResponse>(`/api/admin/class-templates/${encodeURIComponent(template.class_id)}/cutoff-preview?inactiveFromDate=${encodeURIComponent(cutoff)}`)
      .then((value) => { if (current) setPreview(value); })
      .catch((error) => { if (current) setPreviewError(error instanceof Error ? error.message : "Could not preview affected classes"); });
    return () => { current = false; };
  }, [cutoff, template.class_id]);
  async function save(form: HTMLFormElement, clear = false) {
    const value = new FormData(form);
    const result = await mutate(() => post(`/api/admin/class-templates/${encodeURIComponent(template.class_id)}/cutoff`, {
      requestId, inactiveFromDate: clear ? null : cutoff, pin: value.get("pin"),
      reason: clear ? "Regular timetable cutoff cleared" : "Regular timetable cutoff changed",
    }), clear ? "Regular class end date cleared" : "Regular class end date saved");
    if (result) onClose(); else setRequestId(crypto.randomUUID());
  }
  return <div className="modal-backdrop" role="presentation"><section className="confirmation-modal" role="dialog"
    aria-modal="true" aria-labelledby="regular-cutoff-title"><h2 id="regular-cutoff-title">Remove from this date onwards</h2>
    <p><strong>{template.title}</strong></p><form onSubmit={(event) => { event.preventDefault(); void save(event.currentTarget); }}>
      <label><span>First date not to run</span><input type="date" value={cutoff} onChange={(event) => setCutoff(event.target.value)} required /></label>
      {preview && <dl className="confirmation-summary"><div><dt>Unused classes removed</dt><dd>{preview.removable}</dd></div>
        <div><dt>History / cancellations preserved</dt><dd>{preview.preserved}</dd></div></dl>}
      {preview?.affectedSessions.length ? <div><strong>Affected future classes</strong><ul className="affected-session-list">
        {preview.affectedSessions.map((session) => <li key={session.session_id}>{session.class_date} · {session.start_time} · {session.title}</li>)}
      </ul></div> : preview && <p className="muted">No unused future classes will be removed.</p>}
      {previewError && <p className="form-error" role="alert">{previewError}</p>}
      <p className="muted">The cutoff is inclusive: no class is generated on or after {cutoff}. Existing records are preserved.</p>
      <label><span>Confirmation PIN</span><input name="pin" type="password" inputMode="numeric" pattern="[0-9]{4}"
        minLength={4} maxLength={4} autoComplete="off" required /></label>
      <div className="row-actions"><button className="primary-button" disabled={working || !preview}>Save cutoff</button>
        {template.inactive_from_date && <button type="button" className="secondary-button" disabled={working}
          onClick={(event) => { if (event.currentTarget.form) void save(event.currentTarget.form, true); }}>Clear cutoff</button>}
        <button type="button" className="secondary-button" disabled={working} onClick={onClose}>Cancel</button></div>
    </form></section></div>;
}

function DeleteRegularClassModal({ template, working, mutate, onClose }: {
  template: RegularClassTemplate; working: boolean; mutate: Mutate; onClose: () => void;
}) {
  const [requestId, setRequestId] = useState(() => crypto.randomUUID());
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const value = new FormData(event.currentTarget);
    const result = await mutate(() => remove(`/api/admin/class-templates/${encodeURIComponent(template.class_id)}`, {
      requestId, pin: value.get("pin"), reason: "Unused regular class deleted",
    }), "Regular class deleted");
    if (result) onClose(); else setRequestId(crypto.randomUUID());
  }
  return <div className="modal-backdrop" role="presentation"><section className="confirmation-modal" role="dialog"
    aria-modal="true" aria-labelledby="delete-regular-title"><h2 id="delete-regular-title">Delete regular class</h2>
    <p><strong>{template.title}</strong></p><p>This permanently deletes the template and all generated classes only if none has operational history. Otherwise nothing is deleted.</p>
    <form onSubmit={submit}><label><span>Confirmation PIN</span><input name="pin" type="password" inputMode="numeric"
      pattern="[0-9]{4}" minLength={4} maxLength={4} autoComplete="off" required /></label>
      <div className="row-actions"><button className="primary-button danger-action" disabled={working}>Delete regular class</button>
        <button type="button" className="secondary-button" disabled={working} onClick={onClose}>Cancel</button></div></form>
  </section></div>;
}

export function Sessions({ data, canWrite, working, mutate }: SectionProps) {
  const schoolToday = dateInTimeZone(data.settings.timeZone);
  const [classDate, setClassDate] = useState(schoolToday);
  const [detail, setDetail] = useState<SessionAttendanceResponse | null>(null);
  const [detailFocusVersion, setDetailFocusVersion] = useState(0);
  const [editingSession, setEditingSession] = useState<Session | null>(null);
  const [detailError, setDetailError] = useState("");
  async function viewAttendance(sessionId: string, moveFocus = true) {
    setDetailError("");
    try {
      const next = await api<SessionAttendanceResponse>(`/api/admin/sessions/${encodeURIComponent(sessionId)}/attendance`);
      setEditingSession(null);
      setDetail(next);
      if (moveFocus) setDetailFocusVersion((current) => current + 1);
    }
    catch (reason) { setDetailError(reason instanceof Error ? reason.message : "Could not load class attendance"); }
  }
  const detailMutate: Mutate = async (action, success) => {
    const result = await mutate(action, success);
    if (result && detail) await viewAttendance(detail.session.session_id, false);
    return result;
  };
  useEffect(() => { setClassDate(schoolToday); }, [data.settings.timeZone, schoolToday]);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = event.currentTarget; const value = new FormData(form);
    const result = await mutate(() => post<CreateSessionResponse>("/api/admin/sessions", {
      title: value.get("title"), classDate: value.get("classDate"),
      startTime: value.get("startTime"), endTime: value.get("endTime"),
      instructorStaffId: value.get("instructorStaffId") || undefined,
    }), "One-off class created");
    if (result) { form.reset(); setClassDate(dateInTimeZone(data.settings.timeZone)); }
  }
  const upcoming = [...data.sessions].filter((session) => session.class_date >= schoolToday)
    .sort((left, right) => left.class_date.localeCompare(right.class_date)
      || left.start_time.localeCompare(right.start_time));
  const past = [...data.sessions].filter((session) => session.class_date < schoolToday)
    .sort((left, right) => right.class_date.localeCompare(left.class_date)
      || right.start_time.localeCompare(left.start_time));
  return <div className="admin-stack">
    {detailError && <Notice kind="error">{detailError}</Notice>}
    {detail && <ClassAttendanceDetail detail={detail} focusKey={detailFocusVersion} data={data} working={working} mutate={detailMutate} onClose={() => setDetail(null)} />}
    {editingSession && <EditClassForm session={editingSession} working={working} mutate={mutate}
      onCancel={() => setEditingSession(null)} onSaved={() => setEditingSession(null)} />}
    {data.staff.role === "owner" && <RegularTimetable data={data} working={working} mutate={mutate} />}
    {canWrite && <Panel title="One-off Class">
      <SchoolDateContext date={schoolToday} timeZone={data.settings.timeZone} />
      <form className="form-grid" onSubmit={create} onReset={() => setClassDate(dateInTimeZone(data.settings.timeZone))}>
        <Field name="title" label="Class name" required />
        <label><span>Date</span><input name="classDate" type="date" required value={classDate} onChange={(event) => setClassDate(event.target.value)} /></label>
        <Field name="startTime" label="Start" type="time" required />
        <Field name="endTime" label="End" type="time" required />
        <InstructorSelect data={data} />
        <button className="primary-button" disabled={working}>Create one-off class</button>
      </form>
    </Panel>}
    <Panel title="Upcoming Classes"><div className="data-list">{upcoming.length ? upcoming.map((session) =>
      <article className="data-row" key={session.session_id}><div><strong>{session.title}</strong>
        <span className="inline-meta"><span>{session.class_date}</span><span>{session.start_time} to {session.end_time}</span></span>
        <small>{session.teacher_name}</small>
        <small className={"status-badge status-" + session.status}>{session.status === "cancelled" ? "Cancelled" : session.status}</small></div>
        <div className="row-actions"><button className="primary-button compact" onClick={() => void viewAttendance(session.session_id)}>View attendance</button>
          {canWrite && session.status !== "cancelled" && <OpenClassroomDisplayButton sessionId={session.session_id} compact />}
          <SessionActions session={session} data={data} working={working} mutate={mutate}
            onEdit={(target) => { setDetail(null); setEditingSession(target); }}
            onToggleStatus={(target) => void mutate(() => patch("/api/admin/sessions/" + target.session_id,
              { status: target.status === "open" ? "closed" : "open" }))}
            assignmentControl={<InstructorAssignmentControl session={session} data={data} working={working} mutate={mutate} />} />
        </div>
      </article>) : <p className="muted">No upcoming classes.</p>}</div></Panel>
    <Panel title="Past Classes"><div className="data-list">{past.length ? past.map((session) =>
      <article className="data-row" key={session.session_id}><div><strong>{session.title}</strong>
        <span className="inline-meta"><span>{session.class_date}</span><span>{session.start_time} to {session.end_time}</span></span>
        <small>{session.teacher_name}</small>
        <small className={"status-badge status-" + session.status}>{session.status === "cancelled" ? "Cancelled" : session.status}</small></div>
        <div className="row-actions"><button className="primary-button compact" onClick={() => void viewAttendance(session.session_id)}>View attendance</button>
          <SessionActions session={session} data={data} working={working} mutate={mutate}
            onEdit={(target) => { setDetail(null); setEditingSession(target); }} />
        </div>
      </article>) : <p className="muted">No past classes.</p>}</div></Panel>
  </div>;
}

export function ClassAttendanceDetail({ detail, focusKey, data, working, mutate, onClose }: {
  detail: SessionAttendanceResponse; focusKey?: number; data: AdminResponse; working: boolean; mutate: Mutate; onClose: () => void;
}) {
  const { containerRef, headingRef } = useAdminDetailFocus("attendance:" + detail.session.session_id + ":" + String(focusKey ?? 0));
  const [showBackfill, setShowBackfill] = useState(false);
  return <>{showBackfill && <HistoricalAttendanceModal data={data} fixedSessionId={detail.session.session_id}
    working={working} mutate={mutate} onCancel={() => setShowBackfill(false)} />}
    <Panel title="Class Attendance" className="admin-detail" sectionRef={containerRef} headingRef={headingRef}
    headingId="class-attendance-detail-heading"><div className="detail-heading"><div><strong>{detail.session.title}</strong>
    <span className="inline-meta"><span>{detail.session.class_date}</span><span>{detail.session.start_time}–{detail.session.end_time}</span></span>
    <small>{detail.totalCheckedIn} checked in</small></div><div className="row-actions">
      {data.staff.role === "owner" && detail.session.status !== "cancelled" && <button type="button" className="primary-button compact"
        onClick={() => setShowBackfill(true)}>Add student</button>}
      <button className="text-button" onClick={onClose}>Close</button></div></div>
    <div className="data-list">{detail.attendance.length ? detail.attendance.map((row) => <article className="data-row" key={row.attendance_id}>
      <div><strong>{row.display_name}</strong><span>{timeInTimeZone(row.checked_in_at, detail.timeZone)}</span>
        <div className="row-actions"><AttendanceStatusBadge /><AttendanceChargeBadge attendance={row} />
          {row.voided_at && <span className="status-badge status-voided">Voided</span>}</div>
      </div>{!row.voided_at && <AttendanceActions row={row} data={data} working={working} mutate={mutate} />}
    </article>) : <p className="muted">No check-ins yet.</p>}</div>
  </Panel></>;
}

export function StudentHistoryDetail({ data, adminData, focusKey, staffRole = "instructor", working = false,
  mutate, onRefresh, onClose }: {
  data: StudentHistoryResponse; adminData?: AdminResponse; focusKey?: number; staffRole?: AdminResponse["staff"]["role"];
  working?: boolean; mutate?: Mutate; onRefresh?: () => Promise<void>; onClose: () => void;
}) {
  const { language } = useI18n();
  const { containerRef, headingRef } = useAdminDetailFocus("student:" + data.student.student_id + ":" + String(focusKey ?? 0));
  const [extension, setExtension] = useState<PassExtensionPreviewItem | null>(null);
  const [activationPass, setActivationPass] = useState<Pass | null>(null);
  const [showBackfill, setShowBackfill] = useState(false);
  const today = dateInTimeZone(data.timeZone);
  const activePasses = data.passes.filter((pass) => passDisplayStatus(pass, today) === "active");
  const unpaid = data.payments.filter((payment) => payment.status === "unpaid");
  const pending = data.attendance.filter((row) => row.billing_intent === "pass_pending" && !row.voided_at);
  function preview(pass: Pass): PassExtensionPreviewItem {
    return { pass_id: pass.pass_id, student_id: pass.student_id, student_name: data.student.display_name,
      pass_type: pass.pass_type, expiry_date: String(pass.expiry_date), new_expiry_date: passExtendedExpiry(String(pass.expiry_date), 7) };
  }
  async function confirmExtension(pin: string, requestId: string) {
    if (!extension || !mutate) return false;
    const result = await mutate(() => post(`/api/admin/passes/${encodeURIComponent(extension.pass_id)}/extend`, { pin, requestId }),
      language === "zh" ? "課卡已延長 1 星期" : "Pass extended by 1 week");
    if (!result) return false;
    setExtension(null);
    await onRefresh?.();
    return true;
  }
  return <>
    {extension && <PassExtensionModal scope="individual" items={[extension]} language={language}
      onCancel={() => setExtension(null)} onConfirm={confirmExtension} />}
    {activationPass && mutate && <PassActivationModal pass={activationPass} studentName={data.student.display_name}
      attendance={data.attendance} timeZone={data.timeZone} working={working} mutate={mutate}
      onCancel={() => setActivationPass(null)} onSaved={onRefresh} />}
    {showBackfill && adminData && mutate && <HistoricalAttendanceModal data={adminData}
      passes={data.passes} fixedStudentId={data.student.student_id} working={working} mutate={mutate}
      onCancel={() => setShowBackfill(false)} onSaved={onRefresh} />}
    <Panel title="Student Detail" className="admin-detail" sectionRef={containerRef} headingRef={headingRef}
      headingId="student-history-detail-heading"><div className="detail-heading"><div><strong>{data.student.display_name}</strong>
      <span>{data.student.email}</span><small className="inline-meta"><span>{activePasses.length} active pass</span><span>{unpaid.length} unpaid</span><span>{pending.length} pass pending</span></small></div>
      <div className="row-actions">{staffRole === "owner" && adminData && <button type="button" className="primary-button compact"
        onClick={() => setShowBackfill(true)}>{language === "zh" ? "補回上課紀錄" : "Add past attendance"}</button>}
        <button className="text-button" onClick={onClose}>Close</button></div></div>
      <h3>{language === "zh" ? "課卡" : "Passes"}</h3>
      <div className="data-list">{data.passes.length ? data.passes.map((pass) => {
        const status = passDisplayStatus(pass, today);
        return <article className="data-row" key={pass.pass_id}><PassSummary pass={pass} today={today} language={language} />
          <PassCardActions pass={pass} status={status} role={staffRole} language={language} working={working}
            onActivation={() => setActivationPass(pass)} onExtension={() => setExtension(preview(pass))} />
        </article>;
      }) : <p className="muted">{language === "zh" ? "沒有課卡。" : "No passes."}</p>}</div>
      <h3>Class History</h3><div className="data-list">{data.attendance.length ? data.attendance.map((row) => <article className="data-row" key={row.attendance_id}>
        <div><strong>{row.title}</strong><span className="inline-meta"><span>{row.class_date}</span><span>{row.start_time}</span></span>
          <time dateTime={row.checked_in_at}>{timeInTimeZone(row.checked_in_at, data.timeZone)}</time>
          <div className="row-actions"><AttendanceStatusBadge /><AttendanceChargeBadge attendance={row} />
            {row.voided_at && <span className="status-badge status-voided">Voided</span>}</div>
        </div>
      </article>) : <p className="muted">No class history yet.</p>}</div>
    </Panel>
  </>;
}

function PassSummary({ pass, today, language }: { pass: Pass; today: string; language: "zh" | "en" }) {
  const status = passDisplayStatus(pass, today);
  return <div><strong>{pass.pass_type === "unlimited" ? (language === "zh" ? "無限卡" : "Unlimited")
    : `${pass.remaining_classes}/${pass.total_classes}`}</strong>
    <span className={`status-badge status-${status}`}>{passStatusLabel(status, language)}</span>
    {status === "not_activated"
      ? <small>{language === "zh" ? `有效期：首次使用起計 ${pass.validity_days} 日` : `Validity: ${pass.validity_days} days from first check-in`}</small>
      : <small>{language === "zh" ? "最後可用日" : "Usable through"}: {pass.expiry_date || "—"}</small>}
  </div>;
}

function PassCardActions({ pass, status, role, language, working, onActivation, onExtension }: {
  pass: Pass; status: ReturnType<typeof passDisplayStatus>; role: AdminResponse["staff"]["role"];
  language: "zh" | "en"; working: boolean; onActivation: () => void; onExtension: () => void;
}) {
  if (role !== "owner" || pass.status === "cancelled") return null;
  return <ActionMenu>
    <ActionMenuItem disabled={working} onClick={onActivation}>
      {pass.activated_at ? (language === "zh" ? "修改啟用日期" : "Change activation date") : (language === "zh" ? "啟用課卡" : "Activate pass")}
    </ActionMenuItem>
    {status === "active" && pass.expiry_date && <ActionMenuItem disabled={working} onClick={onExtension}>
      {language === "zh" ? "延長 1 星期" : "Extend 1 week"}
    </ActionMenuItem>}
  </ActionMenu>;
}
function InstructorAssignmentControl({ session, data, working, mutate }: { session: Session; data: AdminResponse; working: boolean; mutate: Mutate }) {
  const assigned = data.sessionAssignments?.find((item) => item.session_id === session.session_id)?.staff_id || "";
  const [staffId, setStaffId] = useState(assigned);
  useEffect(() => { setStaffId(assigned); }, [assigned]);
  const instructors = (data.staffMembers || []).filter((staff) => staff.role === "instructor" && staff.active);
  return <div className="action-menu-control">
      <label className="action-menu-field"><span>Assigned instructor</span><select value={staffId} onChange={(event) => setStaffId(event.target.value)}>
        <option value="">Not assigned</option>
        {instructors.map((staff) => <option key={staff.staff_id} value={staff.staff_id}>{staff.display_name}</option>)}
      </select></label>
      <ActionMenuItem disabled={working} onClick={() => void mutate(
        () => put(`/api/admin/sessions/${encodeURIComponent(session.session_id)}/instructor`, { staffId: staffId || null }),
        "Instructor assignment saved",
      )}>Save instructor</ActionMenuItem>
  </div>;
}
function Passes({ data, working, mutate }: SectionProps) {
  const { t, language } = useI18n();
  const [bulkPreview, setBulkPreview] = useState<PassExtensionPreviewItem[] | null>(null);
  const [individualPreview, setIndividualPreview] = useState<PassExtensionPreviewItem | null>(null);
  const [activationPass, setActivationPass] = useState<Pass | null>(null);
  const [previewError, setPreviewError] = useState("");
  const today = dateInTimeZone(data.settings.timeZone);
  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = event.currentTarget; const value = new FormData(form);
    const passType = String(value.get("passType")), total = Number(value.get("totalClasses") || 0), amount = Number(value.get("amount") || 0);
    const result = await mutate(() => post("/api/admin/passes", {
      studentId: value.get("studentId"), passType, totalClasses: passType === "count" ? total : null,
      remainingClasses: passType === "count" ? total : null, amountMinor: amount ? Math.round(amount * 100) : null,
      purchaseDate: value.get("purchaseDate"),
    }), language === "zh" ? "課卡已建立，將於首次使用時啟用" : "Pass created and awaiting first use");
    if (result) form.reset();
  }
  async function openBulkPreview() {
    setPreviewError("");
    try {
      const preview = await api<PassExtensionPreviewResponse>("/api/admin/pass-extensions/preview");
      if (!preview.affectedCount) setPreviewError(language === "zh" ? "目前沒有可延長的有效課卡。" : "There are no active passes to extend.");
      else setBulkPreview(preview.passes);
    } catch (reason) { setPreviewError(reason instanceof Error ? reason.message : t("error")); }
  }
  async function confirmBulk(pin: string, requestId: string) {
    const result = await mutate(() => post("/api/admin/pass-extensions", { pin, requestId }),
      language === "zh" ? "所有有效課卡已延長 1 星期" : "All active passes extended by 1 week");
    if (!result) return false;
    setBulkPreview(null);
    return true;
  }
  async function confirmIndividual(pin: string, requestId: string) {
    if (!individualPreview) return false;
    const result = await mutate(() => post(`/api/admin/passes/${encodeURIComponent(individualPreview.pass_id)}/extend`, { pin, requestId }),
      language === "zh" ? "課卡已延長 1 星期" : "Pass extended by 1 week");
    if (!result) return false;
    setIndividualPreview(null);
    return true;
  }
  return <div className="admin-stack">
    {activationPass && <PassActivationModal pass={activationPass} studentName={studentName(data, activationPass.student_id)}
      attendance={data.attendance} timeZone={data.settings.timeZone} working={working} mutate={mutate}
      onCancel={() => setActivationPass(null)} />}
    {bulkPreview && <PassExtensionModal scope="bulk" items={bulkPreview} language={language}
      onCancel={() => setBulkPreview(null)} onConfirm={confirmBulk} />}
    {individualPreview && <PassExtensionModal scope="individual" items={[individualPreview]} language={language}
      onCancel={() => setIndividualPreview(null)} onConfirm={confirmIndividual} />}
    {previewError && <Notice kind="error">{previewError}</Notice>}
    {data.staff.role === "owner" && <Panel title={t("addPass")}><form className="form-grid" onSubmit={create}>
      <label><span>{t("students")}</span><select name="studentId" required>{data.students.filter((s) => s.status !== "merged").map((s) => <option key={s.student_id} value={s.student_id}>{s.display_name}</option>)}</select></label>
      <label><span>{t("passes")}</span><select name="passType"><option value="count">{t("count")}</option><option value="unlimited">{t("unlimited")}</option></select></label>
      <Field name="totalClasses" label={t("total")} type="number" defaultValue="10" />
      <Field name="amount" label={t("amount")} type="number" />
      <Field name="purchaseDate" label={language === "zh" ? "購買日期" : "Purchase date"} type="date" defaultValue={today} />
      <div className="pass-validity-context">{language === "zh" ? `${"有效期：首次使用起計 "}${data.settings.defaultPassValidityDays} 日` : `Validity: ${data.settings.defaultPassValidityDays} days from first check-in`}</div>
      <button className="primary-button" disabled={working}>{t("save")}</button>
    </form></Panel>}
    <Panel title={t("passes")}>
      {data.staff.role === "owner" && <div className="panel-actions"><button type="button" className="secondary-button"
        disabled={working} onClick={() => void openBulkPreview()}>{language === "zh" ? "所有有效課卡延長 1 星期" : "Extend all active passes by 1 week"}</button></div>}
      <div className="data-list">{data.passes.map((pass) => {
        const status = passDisplayStatus(pass, today);
        const preview = pass.expiry_date ? { pass_id: pass.pass_id, student_id: pass.student_id, student_name: studentName(data, pass.student_id),
          pass_type: pass.pass_type, expiry_date: pass.expiry_date, new_expiry_date: passExtendedExpiry(pass.expiry_date, 7) } : null;
        return <article className="data-row" key={pass.pass_id}>
          <div><PassSummary pass={pass} today={today} language={language} /><span>{studentName(data, pass.student_id)}</span></div>
          <PassCardActions pass={pass} status={status} role={data.staff.role} language={language} working={working}
            onActivation={() => setActivationPass(pass)} onExtension={() => { if (preview) setIndividualPreview(preview); }} />
        </article>;
      })}</div>
    </Panel>
  </div>;
}
function Payments({ data, canWrite, working, mutate }: SectionProps) {
  const { t } = useI18n();
  return <Panel title={t("payments")}><div className="data-list">{data.payments.map((payment) => <article className="data-row" key={payment.payment_id}>
    <div><strong>{studentName(data, payment.student_id)}</strong><span>{money(payment.amount_minor, payment.currency)}</span><small className="inline-meta"><span>{payment.type}</span><span>{payment.status}</span></small></div>
    <PaymentActions payment={payment} data={data} working={working} mutate={mutate} />
  </article>)}</div></Panel>;
}
function Emails({ data }: { data: AdminResponse }) {
  const { t } = useI18n();
  return <Panel title={t("emails")}><div className="data-list">{data.emailEvents.map((event) => <article className="data-row" key={String(event.email_event_id)}>
    <div><strong>{String(event.recipient)}</strong><span>{String(event.subject)}</span><small className="inline-meta"><span>{String(event.kind)}</span><span>{String(event.status)}</span><span>{String(event.created_at)}</span></small></div>
  </article>)}</div></Panel>;
}

export function Staff({ data, working, mutate }: { data: AdminResponse; working: boolean; mutate: SectionProps["mutate"] }) {
  const { language } = useI18n();
  type StaffMember = NonNullable<AdminResponse["staffMembers"]>[number];
  const [change, setChange] = useState<{ staff: StaffMember; role: "owner" | "instructor"; active: boolean; requestId: string } | null>(null);
  const accessReminder = language === "zh"
    ? "此電郵亦需要加入 Cloudflare Access 的允許名單，才能登入員工系統。"
    : "Staff access also requires this email to be allowed in the Cloudflare Access policy.";
  const roleLabel = (role: "owner" | "instructor") => language === "zh"
    ? (role === "owner" ? "管理員" : "導師") : (role === "owner" ? "Owner" : "Instructor");
  function openChange(staff: StaffMember, role: "owner" | "instructor", active: boolean) {
    setChange({ staff, role, active, requestId: crypto.randomUUID() });
  }
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault(); const form = event.currentTarget; const value = new FormData(form);
    const role = String(value.get("role")) as "owner" | "instructor";
    const result = await mutate(() => post("/api/admin/staff", {
      displayName: value.get("displayName"), email: value.get("email"), role,
      ...(role === "owner" ? { pin: value.get("pin") } : {}),
    }), "Staff access saved. " + accessReminder);
    if (result) form.reset();
  }
  async function confirmChange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!change) return;
    const value = new FormData(event.currentTarget);
    const result = await mutate(() => patch("/api/admin/staff/" + encodeURIComponent(change.staff.staff_id), {
      role: change.role, active: change.active, pin: value.get("pin"), requestId: change.requestId,
    }), "Staff access updated. " + accessReminder);
    if (result) setChange(null);
  }
  return <div className="admin-stack">
    {change && <div className="modal-backdrop" role="presentation"><section className="confirmation-modal" role="dialog" aria-modal="true" aria-labelledby="staff-change-title">
      <h2 id="staff-change-title">{language === "zh" ? "確認員工權限修改" : "Confirm staff access change"}</h2>
      <p><strong>{change.staff.display_name}</strong><br />{change.staff.email}</p>
      <dl className="confirmation-summary"><div><dt>{language === "zh" ? "目前" : "Current"}</dt><dd>{roleLabel(change.staff.role)} · {change.staff.active ? "Active" : "Disabled"}</dd></div>
        <div><dt>{language === "zh" ? "修改後" : "New"}</dt><dd>{roleLabel(change.role)} · {change.active ? "Active" : "Disabled"}</dd></div></dl>
      <form onSubmit={confirmChange}><label><span>{language === "zh" ? "確認 PIN" : "Confirmation PIN"}</span><input name="pin" type="password" inputMode="numeric" pattern="[0-9]{4}" minLength={4} maxLength={4} autoComplete="off" required autoFocus /></label>
        <div className="row-actions"><button className="primary-button" disabled={working}>{language === "zh" ? "確認修改" : "Confirm change"}</button><button type="button" className="secondary-button" disabled={working} onClick={() => setChange(null)}>{language === "zh" ? "取消" : "Cancel"}</button></div></form>
    </section></div>}
    <Panel title={language === "zh" ? "新增員工權限" : "Add staff access"}>
      <Notice>{accessReminder}</Notice>
      <form className="form-grid" onSubmit={save}>
        <Field name="displayName" label={language === "zh" ? "顯示名稱" : "Display name"} required /><Field name="email" label={language === "zh" ? "電郵" : "Email"} type="email" required />
        <label><span>{language === "zh" ? "角色" : "Role"}</span><select name="role" defaultValue="instructor"><option value="instructor">{roleLabel("instructor")}</option><option value="owner">{roleLabel("owner")}</option></select></label>
        <label><span>{language === "zh" ? "確認 PIN（新增管理員時需要）" : "Confirmation PIN (required for Owner)"}</span><input name="pin" type="password" inputMode="numeric" pattern="[0-9]{4}" minLength={4} maxLength={4} autoComplete="off" /></label>
        <button className="primary-button" disabled={working}>{language === "zh" ? "儲存員工" : "Save staff"}</button>
      </form>
    </Panel>
    <Panel title={language === "zh" ? "員工角色" : "Staff roles"}><div className="data-list">{(data.staffMembers || []).map((staff) =>
      <article className="data-row" key={staff.staff_id}><div><strong>{staff.display_name}</strong><span>{staff.email}</span><small className="inline-meta"><span>{roleLabel(staff.role)}</span><span>{staff.active ? "active" : "disabled"}</span>{staff.is_primary_owner ? <span>Primary owner</span> : null}</small></div>
        <div className="row-actions">{staff.is_primary_owner ? <span className="muted">{language === "zh" ? "主要管理員受保護" : "Primary owner protected"}</span> :
          <ActionMenu>
            <ActionMenuItem disabled={working} onClick={() => openChange(staff, staff.role === "owner" ? "instructor" : "owner", Boolean(staff.active))}>
              {language === "zh" ? "更改角色" : "Change role"}
            </ActionMenuItem>
            {staff.active && <ActionMenuDivider />}
            <ActionMenuItem className={staff.active ? "danger-action" : ""} disabled={working}
              onClick={() => openChange(staff, staff.role, !staff.active)}>
              {staff.active ? (language === "zh" ? "停用" : "Deactivate") : (language === "zh" ? "重新啟用" : "Reactivate")}
            </ActionMenuItem>
          </ActionMenu>}</div>
      </article>
    )}</div></Panel>
  </div>;
}interface SectionProps { data: AdminResponse; canWrite: boolean; working: boolean; mutate: Mutate }
function Panel({ title, children, className = "", sectionRef, headingRef, headingId }: {
  title: string; children: ReactNode; className?: string; sectionRef?: Ref<HTMLElement>;
  headingRef?: Ref<HTMLHeadingElement>; headingId?: string;
}) { return <section className={`admin-panel ${className}`.trim()} ref={sectionRef} aria-labelledby={headingId}>
  <h2 id={headingId} ref={headingRef} tabIndex={headingRef ? -1 : undefined}>{title}</h2>{children}</section>; }
function Stat({ label, value }: { label: string; value: number }) { return <article className="stat-card"><span>{label}</span><strong>{value}</strong></article>; }
function Follow({ label, value }: { label: string; value: string }) { return <div className="follow-row"><span>{label}</span><strong>{value}</strong></div>; }
function Empty() { return <p className="muted">Nothing to follow up.</p>; }
function AttendanceList({ rows, data, working, mutate }: { rows: AdminResponse["attendance"]; data: AdminResponse; working: boolean; mutate: Mutate }) { return <div className="data-list">{rows.map((row) => <div className="data-row" key={row.attendance_id}><div><strong>{row.display_name}</strong><span>{row.title}</span><AttendanceMeta dateTime={row.checked_in_at} chargeType={row.charge_type} billingIntent={row.billing_intent} /></div><AttendanceActions row={row} data={data} working={working} mutate={mutate} /></div>)}</div>; }
function Field({ name, label, type = "text", required, defaultValue }: { name: string; label: string; type?: string; required?: boolean; defaultValue?: string }) {
  return <label><span>{label}</span><input name={name} type={type} required={required} defaultValue={defaultValue} /></label>;
}
function parseStudentCsv(text: string) {
  const lines = text.replace(/^\uFEFF/, "").split(/\r?\n/).filter((line) => line.trim());
  if (lines.length < 2) throw new Error("CSV needs a header row and at least one student.");
  const rows = lines.map(parseCsvLine), headers = rows[0].map((value) => value.trim().toLowerCase().replace(/\s+/g, "_"));
  const find = (row: string[], names: string[]) => {
    const index = headers.findIndex((header) => names.includes(header));
    return index >= 0 ? (row[index] || "").trim() : "";
  };
  return rows.slice(1).map((row) => ({
    displayName: find(row, ["display_name", "name", "student_name", "學生姓名", "姓名"]),
    email: find(row, ["email", "email_address", "電郵", "電子郵件"]),
    phone: find(row, ["phone", "telephone", "?餉店"]),
    emailConsent: !["no", "false", "0"].includes(find(row, ["email_consent", "reminders"]).toLowerCase()),
  })).filter((student) => student.displayName && student.email);
}
function parseCsvLine(line: string): string[] {
  const cells: string[] = []; let cell = "", quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index], next = line[index + 1];
    if (char === '"' && quoted && next === '"') { cell += '"'; index += 1; }
    else if (char === '"') quoted = !quoted;
    else if (char === "," && !quoted) { cells.push(cell); cell = ""; }
    else cell += char;
  }
  cells.push(cell); return cells;
}function studentName(data: AdminResponse, id: string) { return data.students.find((student) => student.student_id === id)?.display_name || id; }
function money(value: number, currency: string) { return currency + " " + (value / 100).toFixed(2); }
function daysUntil(date: string, today: string) { return Math.ceil((Date.parse(date + "T00:00:00Z") - Date.parse(today + "T00:00:00Z")) / 86_400_000); }
